import ImageUploader from "../components/ImageUploader";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "./api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Plus, Trash2, Save, Sparkles, ImagePlus, Upload, X } from "lucide-react";

const EMPTY = {
  id: null,
  name: "",
  bloom_size: "",
  height: "",
  colour: "",
  tile_image: "",
  image: "",
  description: "",
  sort_order: 0,
  published: true,
};

export default function ConsoleCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or item
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSel = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const allSelected = items.length > 0 && items.every((v) => selected.has(v.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(items.map((v) => v.id)));

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!window.confirm(`Delete ${ids.length} variet${ids.length === 1 ? "y" : "ies"} from the catalogue?\n\nThis can't be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/catalog/bulk-delete", { ids });
      toast.success(`Deleted ${r.data?.deleted ?? ids.length} varieties.`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/admin/catalog");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Couldn't load the catalog.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (v) => setEditing({ ...v });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const payload = {
      name: editing.name.trim(),
      bloom_size: editing.bloom_size.trim(),
      height: editing.height.trim(),
      colour: editing.colour.trim(),
      tile_image: (editing.tile_image || "").trim(),
      image: editing.image.trim(),
      description: editing.description.trim(),
      sort_order: Number(editing.sort_order) || 0,
      published: !!editing.published,
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/catalog/${editing.id}`, payload);
        toast.success("Variety updated.");
      } else {
        await api.post("/admin/catalog", payload);
        toast.success("Variety added.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Save failed. Check the fields.");
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/catalog/${v.id}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div data-testid="admin-catalog" className="space-y-8 max-w-5xl">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
            ✦ THE CATALOG ✦
          </span>
          <h1 className="mt-2 font-display text-4xl text-[var(--dahlias-forest)]">
            Varieties on the farm
          </h1>
          <p className="mt-2 text-base text-[var(--dahlias-ink)]/70 max-w-xl">
            Add each variety blooming this season. These show up on the public catalog
            page where visitors can register interest in specific blooms.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={async () => {
              if (!window.confirm("Add the 98 default Heritage varieties to this catalog?\n\nExisting entries (matched by name, case-insensitive) are left alone. Only missing varieties are created.\n\nUse this on a fresh environment (e.g. after a production deploy) to bring the catalog up to par.")) return;
              try {
                const r = await api.post("/admin/catalog/seed-defaults");
                const created = r.data?.created || 0;
                if (created === 0) {
                  toast.success(`All 98 varieties already present (${r.data?.total_in_catalog} total).`);
                } else {
                  toast.success(`Created ${created} missing varieties. Catalog now has ${r.data?.total_in_catalog}.`);
                }
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Seed failed.");
              }
            }}
            data-testid="admin-catalog-seed-defaults"
            className="h-12 px-5 bg-[var(--dahlias-forest)]/10 hover:bg-[var(--dahlias-forest)]/20 text-[var(--dahlias-forest)] border border-[var(--dahlias-forest)]/30 wobble-pill font-medium inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" /> Seed default 98
          </Button>
          <Link
            to="/admin/catalog/bulk"
            data-testid="admin-catalog-bulk-link"
            className="h-12 px-5 bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white wobble-pill font-medium tracking-wide inline-flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Bulk upload
          </Link>
          <Button
            type="button"
            onClick={async () => {
              if (!window.confirm("Remove non-dahlia CSV junk (shipping rows, coasters, aprons, towels, seed mixes, vouchers, freight, etc.)?\n\nThis can't be undone, but it only removes things that look like junk.")) return;
              try {
                const r = await api.post("/admin/catalog/cleanup-junk");
                toast.success(`Removed ${r.data?.removed || 0} junk rows.`);
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Cleanup failed.");
              }
            }}
            data-testid="admin-catalog-cleanup-junk"
            className="h-12 px-5 bg-[var(--dahlias-rust)]/10 hover:bg-[var(--dahlias-rust)]/25 text-[var(--dahlias-rust)] border border-[var(--dahlias-rust)]/30 wobble-pill font-medium inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Remove junk
          </Button>
          <Button
            type="button"
            onClick={startNew}
            data-testid="admin-catalog-new"
            className="h-12 px-6 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add variety
          </Button>
        </div>
      </header>

      {editing && <CatalogForm editing={editing} setEditing={setEditing} onSave={save} onCancel={cancel} />}

      {items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="catalog-select-all" className="w-4 h-4 accent-[var(--berry)]" />
            <span className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">Select all ({items.length})</span>
          </label>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="catalog-bulk-bar">
              <span className="text-xs text-[var(--dahlias-ink)]/70">{selected.size} selected</span>
              <Button
                type="button"
                onClick={bulkDelete}
                disabled={bulkBusy}
                data-testid="catalog-bulk-delete"
                className="h-9 px-4 text-sm bg-[var(--dahlias-rust)] hover:bg-[var(--dahlias-rust)]/85 text-white wobble-pill inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete selected
              </Button>
              <button type="button" onClick={() => setSelected(new Set())} data-testid="catalog-bulk-clear" className="text-xs underline underline-offset-2 text-[var(--dahlias-ink)]/60">
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-[var(--dahlias-ink)]/60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 wobble-a p-10 text-center">
          <Sparkles className="w-9 h-9 mx-auto text-[var(--dahlias-sage-deep)]/50" />
          <p className="mt-3 font-display text-2xl text-[var(--dahlias-forest)]">
            No varieties added yet.
          </p>
          <p className="mt-2 text-[var(--dahlias-ink)]/70">
            Tap &ldquo;Add variety&rdquo; to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((v) => (
            <article
              key={v.id}
              data-testid={`admin-catalog-row-${v.id}`}
              className={`bg-[var(--dahlias-paper)] border-2 wobble-b p-4 flex gap-4 relative ${selected.has(v.id) ? "border-[var(--berry)]" : "border-[var(--dahlias-sage)]/25"}`}
            >
              <input
                type="checkbox"
                checked={selected.has(v.id)}
                onChange={() => toggleSel(v.id)}
                data-testid={`catalog-select-${v.id}`}
                className="absolute top-3 right-3 w-4 h-4 accent-[var(--berry)] cursor-pointer"
              />
              <div
                className="w-24 h-28 shrink-0 bg-cover bg-center bg-[var(--dahlias-sage)]/15 wobble-c"
                style={v.image ? { backgroundImage: `url(${v.image})` } : undefined}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl text-[var(--dahlias-forest)] truncate">
                  {v.name}
                </h3>
                <p className="text-sm text-[var(--dahlias-ink)]/70 mt-1 line-clamp-2">
                  {[v.bloom_size, v.height, v.colour].filter(Boolean).join(" · ") || "-"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() => startEdit(v)}
                    data-testid={`admin-catalog-edit-${v.id}`}
                    className="h-9 px-4 text-sm bg-[var(--dahlias-cream)] hover:bg-[var(--dahlias-sage)]/15 text-[var(--dahlias-ink)] border border-[var(--dahlias-sage)]/30 wobble-pill"
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    onClick={() => remove(v)}
                    data-testid={`admin-catalog-delete-${v.id}`}
                    className="h-9 px-4 text-sm bg-[var(--dahlias-rust)]/10 hover:bg-[var(--dahlias-rust)]/20 text-[var(--dahlias-rust)] border border-[var(--dahlias-rust)]/25 wobble-pill inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                  {!v.published && (
                    <span className="inline-flex items-center px-3 h-9 text-xs font-label tracking-[0.3em] bg-[var(--dahlias-ink)]/10 text-[var(--dahlias-ink)]/65 wobble-pill">
                      DRAFT
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function CatalogForm({ editing, setEditing, onSave, onCancel }) {
  const f = (k) => (e) =>
    setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div
      data-testid="admin-catalog-form"
      className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/40 wobble-a p-6 md:p-7 space-y-5"
    >
      <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">
        {editing.id ? "Edit variety" : "Add a variety"}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name *" tid="cat-name">
          <Input
            data-testid="admin-catalog-input-name"
            value={editing.name}
            onChange={f("name")}
            placeholder="Café au Lait"
            className="h-11 wobble-pill"
          />
        </Field>
        <Field label="Catalog tile image (square, main page)" tid="cat-tile">
          <ImageUploader value={editing.tile_image || ""} onChange={(url) => setEditing({ ...editing, tile_image: url })} label="" testid="admin-catalog-tile" group="products" />
        </Field>
        <Field label="Detail page photo (large, variety page)" tid="cat-image">
          <ImageUploader value={editing.image || ""} onChange={(url) => setEditing({ ...editing, image: url })} label="" testid="admin-catalog-image" group="products" />
        </Field>
        <Field label="Bloom size" tid="cat-size">
          <Input
            data-testid="admin-catalog-input-size"
            value={editing.bloom_size}
            onChange={f("bloom_size")}
            placeholder='e.g. 18 cm or "Dinner-plate"'
            className="h-11 wobble-pill"
          />
        </Field>
        <Field label="Height" tid="cat-height">
          <Input
            data-testid="admin-catalog-input-height"
            value={editing.height}
            onChange={f("height")}
            placeholder="120 cm"
            className="h-11 wobble-pill"
          />
        </Field>
        <Field label="Colour" tid="cat-colour">
          <Input
            data-testid="admin-catalog-input-colour"
            value={editing.colour}
            onChange={f("colour")}
            placeholder="Soft latte / cream / blush"
            className="h-11 wobble-pill"
          />
        </Field>
        <Field label="Sort order" tid="cat-sort">
          <Input
            type="number"
            data-testid="admin-catalog-input-sort"
            value={editing.sort_order}
            onChange={f("sort_order")}
            className="h-11 wobble-pill"
          />
        </Field>
      </div>

      <Field label="Description (optional)" tid="cat-desc">
        <Textarea
          data-testid="admin-catalog-input-desc"
          value={editing.description}
          onChange={f("description")}
          rows={3}
          className="wobble-a"
          placeholder="A line or two for visitors browsing the catalog."
        />
      </Field>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="admin-catalog-input-published"
          checked={!!editing.published}
          onChange={f("published")}
          className="w-4 h-4"
        />
        Published (visible on public catalog)
      </label>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          onClick={onSave}
          data-testid="admin-catalog-save"
          className="h-11 px-6 bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> Save
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          data-testid="admin-catalog-cancel"
          className="h-11 px-6 bg-transparent text-[var(--dahlias-ink)] border border-[var(--dahlias-sage)]/30 hover:bg-[var(--dahlias-sage)]/10 wobble-pill"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children, icon: Icon }) {
  return (
    <div>
      <Label className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--dahlias-sage-deep)] flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

